import React, { useCallback, useEffect, useRef, useState } from 'react';
import QuestionCard from './QuestionCard.jsx';
import ContextModal from './ContextModal.jsx';
import { fetchSectionForCategory } from '../data/sections.js';
import { submitDailySet } from '../util/playContract.js';
import { unlockAudio, playSubmitFanfare } from '../util/sound.js';

// One category's back section as a full page — reached from the sections
// dropdown (web parity W4, refined same day on Tom's first pass). The page is
// the category's WHOLE story for the day:
//
//  - The category's DAILY-SIX question leads, pinned (Tom: "sections should
//    include the one daily six question in that category"). Locked when the
//    six is already submitted; before that, answering it here feeds the
//    ballot through `onAnswerSix` — the pick travels back to Today. It is
//    NEVER written from this page: the six submits only from Today, keeping
//    one write path (the all-six award gate depends on that).
//  - Then the section questions. ONE answer enables Submit; unanswered
//    questions are left alone — **never written**. The server counts section
//    participation by vote-doc EXISTENCE (`sectionAnsweredCount =
//    votedIds.size` opens the streak gate), so a "skip" vote doc would count
//    a non-answer as participation. The six writes skips on purpose;
//    sections must not.
//  - Unanswered questions STAY OPEN: after a submit they remain answerable,
//    and a later visit can answer + submit them (iOS behavior).
//  - The header line is the six's own progress idiom — "N of M answered —
//    tap YES or NO, then Submit" (Tom: no instructional paragraph) — counting
//    every card on the page, pinned six question included.
//  - Answering snap-scrolls to the next open card, and submit gets the
//    fanfare + a celebration card, both matching the six.
//  - Sections pay like the six since 2026-08-01 (+1/answer, +6/correct —
//    all server-side) but still carry no double-down chip (D4).
export default function SectionPage({ category, uid, sixQuestion, sixLocked, onAnswerSix }) {
  const [questions, setQuestions] = useState(null); // null = loading
  const [submitting, setSubmitting] = useState(false);
  const [justLocked, setJustLocked] = useState(0); // count from the last submit
  const [error, setError] = useState(null);
  const [contextIndex, setContextIndex] = useState(null); // index into the RENDERED list
  const cardRefs = useRef([]);
  const topRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchSectionForCategory(category, uid)
      .then((qs) => alive && setQuestions(qs))
      .catch(() => alive && setQuestions([]));
    return () => {
      alive = false;
    };
  }, [category, uid]);

  // The rendered list: the pinned six question (when it exists) then the
  // section questions. `pinnedCount` converts a rendered index into a
  // `questions` index everywhere below.
  const pinned = sixQuestion ? [sixQuestion] : [];
  const pinnedCount = pinned.length;
  const rendered = [...pinned, ...(questions || [])];

  // Snap-scroll to the next OPEN card after an answer — the six's behavior
  // (Tom: "the screen does not automatically scroll to the next question").
  // Next tick, so the answered state has painted before the scroll measures.
  const scrollToNextOpen = useCallback((fromRenderedIdx, updatedRendered) => {
    setTimeout(() => {
      for (let j = fromRenderedIdx + 1; j < updatedRendered.length; j += 1) {
        const q = updatedRendered[j];
        const open = !q.answered && typeof q.answer !== 'boolean';
        if (open) {
          cardRefs.current[j]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
    }, 60);
  }, []);

  // Local pick on a not-yet-written SECTION question (`answered` marks a
  // vote that already exists server-side — those are locked).
  //
  // Computed from `questions` DIRECTLY, not inside a setQuestions updater:
  // React 18 runs updater functions during the re-render, not at call time,
  // so the first version's "capture `next` inside the updater, scroll after"
  // never saw a non-null `next` and the snap-scroll silently did nothing
  // (Tom caught it live, same day). Play.jsx's handleAnswer computes from
  // current state for exactly this reason.
  const handleSectionAnswer = useCallback(
    (sectionIndex, answer) => {
      const next = questions.map((q, i) =>
        i === sectionIndex && !q.answered ? { ...q, answer, isSkipped: false } : q
      );
      setQuestions(next);
      scrollToNextOpen(pinnedCount + sectionIndex, [...pinned, ...next]);
    },
    [questions, pinnedCount, pinned, scrollToNextOpen]
  );

  // The pinned six question routes to the ballot's state via the parent.
  const handlePinnedAnswer = useCallback(
    (answer) => {
      onAnswerSix?.(answer);
      scrollToNextOpen(0, [
        ...(sixQuestion ? [{ ...sixQuestion, answer }] : []),
        ...(questions || []),
      ]);
    },
    [onAnswerSix, scrollToNextOpen, sixQuestion, questions]
  );

  const pending = (questions || []).filter(
    (q) => !q.answered && typeof q.answer === 'boolean'
  );

  const handleSubmit = useCallback(async () => {
    if (!pending.length || submitting) return;
    setSubmitting(true);
    setError(null);
    // A user gesture — the one place the autoplay policy lets audio unlock.
    unlockAudio();
    try {
      // ONLY the newly-answered section questions — see the header comment.
      // The pinned six question is never in `pending` (it lives in Play's
      // state, not `questions`). Award-safe: `awardSubmissionPoints` counts
      // votes only on dailySets/{today}.questionIds.
      await submitDailySet(uid, pending);
      setJustLocked(pending.length);
      setQuestions((prev) =>
        prev.map((q) =>
          !q.answered && typeof q.answer === 'boolean' ? { ...q, answered: true } : q
        )
      );
      // The celebration, matching the six: fanfare + the gold card scrolled
      // into view (it mounts at the top of the page).
      playSubmitFanfare();
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      console.error('[section-page] submit failed:', e);
      setError('Could not save those. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }, [pending, submitting, uid]);

  if (questions === null) {
    return (
      <div className="ps-center" style={{ padding: 40 }}>
        <span className="ps-spinner" aria-label="Loading section" />
      </div>
    );
  }

  if (rendered.length === 0) {
    return (
      <div className="ps-card ps-center">
        <p style={{ fontWeight: 800, fontSize: 17, marginTop: 0 }}>
          No {category} section today
        </p>
        <p className="ps-muted" style={{ margin: 0, lineHeight: 1.5 }}>
          Sections publish alongside the daily six. Check back tomorrow.
        </p>
      </div>
    );
  }

  // "N of M answered" counts EVERY card on the page — the pinned six question
  // (answered or locked either way it has a call) and the section questions
  // (server-written or locally picked).
  const isCounted = (q) => q.answered || typeof q.answer === 'boolean';
  const answeredCount = rendered.filter(isCounted).length;
  const openCount = rendered.length - answeredCount;

  return (
    <div ref={topRef} style={{ scrollMarginTop: 12 }}>
      {/* No "The {category} section" header (Tom 2026-07-30, same-day
          removal) — the dropdown directly above already names where you are,
          so the h2 + icon were saying it twice. The page opens on the
          progress line, like Today. */}

      {/* The six's progress idiom, sized to this page (Tom 2026-07-30: no
          instruction paragraph — "0 of N answered" like the daily six).
          Hidden once everything is answered, exactly like the six's. */}
      {openCount > 0 && (
        <p className="ps-muted" style={{ margin: '0 0 14px', textAlign: 'center' }}>
          {`${answeredCount} of ${rendered.length} answered — tap YES or NO, then Submit`}
        </p>
      )}

      {justLocked > 0 && (
        <div
          className="ps-card ps-center"
          role="status"
          style={{
            marginBottom: 14,
            padding: '16px 16px',
            border: '1.5px solid var(--gold)',
            animation: 'ps-pop-in 0.35s ease-out',
          }}
        >
          <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: 'var(--gold)' }}>
            {justLocked === 1 ? 'Call locked in 🔒' : `${justLocked} calls locked in 🔒`}
          </p>
        </div>
      )}

      {rendered.map((q, ri) => {
        const isPinned = ri < pinnedCount;
        // The MORE-ON-THIS-BEAT label retired 2026-08-13 (Tom) — lockstep
        // with iOS; beat structure lives in Stories now.
        return (
          <div key={q.id} ref={(el) => (cardRefs.current[ri] = el)}>
            <QuestionCard
              question={q}
              index={ri}
              disabled={isPinned ? sixLocked || !onAnswerSix : q.answered || submitting}
              onAnswer={
                isPinned
                  ? (ans) => handlePinnedAnswer(ans)
                  : (ans) => handleSectionAnswer(ri - pinnedCount, ans)
              }
              onContext={() => setContextIndex(ri)}
            />
          </div>
        );
      })}

      {error && (
        <p role="alert" style={{ margin: '0 0 8px', color: 'var(--danger)', fontWeight: 700, fontSize: 13, textAlign: 'center' }}>
          {error}
        </p>
      )}

      {questions.some((q) => !q.answered) && (
        <button
          onClick={handleSubmit}
          disabled={pending.length === 0 || submitting}
          style={{
            width: '100%',
            padding: '15px 0',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: pending.length > 0 ? 'var(--gold)' : 'var(--surface)',
            color: pending.length > 0 ? '#1a1a1a' : 'var(--secondary-text)',
            fontWeight: 800,
            fontSize: 16,
            cursor: pending.length > 0 && !submitting ? 'pointer' : 'default',
          }}
        >
          {submitting
            ? 'Saving…'
            : pending.length > 0
              ? `Submit ${pending.length} ${pending.length === 1 ? 'call' : 'calls'}`
              : 'Answer any to submit'}
        </button>
      )}

      {contextIndex !== null && rendered[contextIndex] && (
        <ContextModal
          question={rendered[contextIndex]}
          onAnswer={
            contextIndex < pinnedCount
              ? sixLocked || !onAnswerSix
                ? undefined
                : (ans) => handlePinnedAnswer(ans)
              : rendered[contextIndex].answered || submitting
                ? undefined
                : (ans) => handleSectionAnswer(contextIndex - pinnedCount, ans)
          }
          onClose={() => setContextIndex(null)}
        />
      )}
    </div>
  );
}
